import createReducer from 'common/redux/utils/createReducer';
import keyBy from 'lodash.keyby';
import Immutable from 'immutable';
import uid from 'uid';
import { orderCollection, orderCollectionPostDelete } from 'utils/ordering';
import * as actions from 'tip/actions/chapters';
import * as tipActions from 'tip/actions/tip';
import * as paragraphActions from 'tip/actions/paragraphs';

const initialState = {
  chapters: Immutable.Map(),
  paragraphs: Immutable.Map(),
};

const paragraphsForChapter = (state, chapter) =>
  state.paragraphs.filter(p => p.chapter.toString() === chapter.toString());

function createParagraph(state, chapter, content) {
  const paragraph = {
    id: uid(),
    order: paragraphsForChapter(state, chapter).size + 1,
    chapter,
    new: true,
    ...content,
  };

  return {
    ...state,
    paragraphs: state.paragraphs.set(paragraph.id, paragraph),
  };
}

function reorderChapters(state, id, thunk) {
  const toUpdate = state.chapters.get(id.toString());
  if (toUpdate) {
    return {
      ...state,
      chapters: orderCollection(
        state.chapters,
        id,
        toUpdate.order,
        thunk(toUpdate.order)
      ),
    };
  }

  return state;
}

function reorderParagraphs(state, paragraph, thunk) {
  const toUpdate = state.paragraphs.get(paragraph.id.toString());
  if (toUpdate) {
    return {
      ...state,
      paragraphs: orderCollection(
        state.paragraphs,
        paragraph.id,
        toUpdate.order,
        thunk(toUpdate.order),
        p => p.chapter.toString() === paragraph.chapter.toString()
      ),
    };
  }

  return state;
}

const chapters = createReducer(initialState, {
  [tipActions.REQUEST_TIP](state, action) {
    return initialState;
  },
  [tipActions.RECEIVE_TIP](state, { tip }) {
    // Flattening paragraphs
    const paragraphs = tip.chapters
      .map(c =>
        c.paragraphs.map(p => {
          return {
            ...p,
            chapter: c.id,
          };
        })
      )
      .reduce((a, b) => a.concat(b), []);

    return {
      ...state,
      chapters: Immutable.Map(keyBy(tip.chapters, 'id')),
      paragraphs: Immutable.Map(keyBy(paragraphs, 'id')),
    };
  },
  [actions.CREATE_CHAPTER](state, { name }) {
    const chapter = {
      id: uid(),
      name,
      order: state.chapters.size + 1,
      new: true,
    };

    return {
      ...state,
      chapters: state.chapters.set(chapter.id, chapter),
    };
  },
  [actions.UPDATE_CHAPTER](state, { id, name }) {
    return {
      ...state,
      chapters: state.chapters.update(id.toString(), c => {
        return {
          ...c,
          name,
        };
      }),
    };
  },
  [actions.MOVE_CHAPTER_UP](state, { chapter }) {
    return reorderChapters(state, chapter, order => order - 1);
  },
  [actions.MOVE_CHAPTER_DOWN](state, { chapter }) {
    return reorderChapters(state, chapter, order => order + 1);
  },
  [actions.REMOVE_CHAPTER](state, { chapter }) {
    return {
      ...state,
      chapters: orderCollectionPostDelete(
        state.chapters.remove(chapter.id.toString()),
        chapter.order
      ),
    };
  },
  [paragraphActions.CREATE_TEXT_PARAGRAPH](
    state,
    { chapter, fields: { content } }
  ) {
    return createParagraph(state, chapter, { content });
  },
  [paragraphActions.CREATE_IMAGE_PARAGRAPH](
    state,
    { chapter, image, thumbnail }
  ) {
    return createParagraph(state, chapter, {
      image,
      thumbnail,
      imagePartId: uid(),
    });
  },
  [paragraphActions.UPDATE_TEXT_PARAGRAPH](state, { id, fields }) {
    return {
      ...state,
      paragraphs: state.paragraphs.update(id.toString(), p => {
        return {
          ...p,
          ...fields,
        };
      }),
    };
  },
  [paragraphActions.MOVE_PARAGRAPH_UP](state, { paragraph }) {
    return reorderParagraphs(state, paragraph, order => order - 1);
  },
  [paragraphActions.MOVE_PARAGRAPH_DOWN](state, { paragraph }) {
    return reorderParagraphs(state, paragraph, order => order + 1);
  },
  [paragraphActions.REMOVE_PARAGRAPH](state, { paragraph }) {
    return {
      ...state,
      paragraphs: orderCollectionPostDelete(
        state.paragraphs.remove(paragraph.id.toString()),
        paragraph.order
      ),
    };
  },
});

export default chapters;



// WEBPACK FOOTER //
// ./src/modules/tip/reducers/chapters.js